import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { api, type ProspectListOverview } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { collectionPath, pathForTenant } from "../../../contexts/TenantContext.tsx";
import { formatNumber } from "../../../utils/format.ts";
import AgentActivity from "../../AgentActivity.tsx";
import LinkTile from "../../LinkTile.tsx";
import Panel from "../../Panel.tsx";
import Sparkline from "../../Sparkline.tsx";
import UserAvatar from "../../UserAvatar.tsx";
import DeltaChip from "../DeltaChip.tsx";

const SHOWN = 6;

function activityKey(r: ProspectListOverview): number {
  return Date.parse(r.last_entry_at ?? r.created_at) || 0;
}

interface RecentListsPanelProps {
  tenants: { id: string; slug: string }[];
  /** Where "All collections" goes: the selected tenant's collections. */
  allListsTo: string;
}

/** The prospect lists with the latest additions, across the org's tenants. Hidden without any. */
export default function RecentListsPanel({ tenants, allListsTo }: RecentListsPanelProps) {
  const tenantIds = useMemo(() => tenants.map((t) => t.id), [tenants]);
  const { data, isLoading } = useQuery({
    queryKey: keys.prospectList.overview(tenantIds, false),
    queryFn: () => api.getProspectListsOverview(tenantIds, false),
    enabled: tenantIds.length > 0,
  });
  const slugs = useMemo(() => new Map(tenants.map((t) => [t.id, t.slug])), [tenants]);
  const recent = useMemo(
    () => [...(data ?? [])].sort((a, b) => activityKey(b) - activityKey(a)).slice(0, SHOWN),
    [data],
  );
  if (!isLoading && recent.length === 0) return null;

  return (
    <Panel
      title="Recently active collections"
      subtitle="The collections with the latest additions"
      action={
        <Button component={Link} to={allListsTo} size="small">
          All collections
        </Button>
      }
    >
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        {isLoading
          ? [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={84} />)
          : recent.map((row) => (
              <ProspectListTile key={row.id} row={row} to={pathForTenant(slugs.get(row.tenant_id), collectionPath(row.id))} />
            ))}
      </Box>
    </Panel>
  );
}

/** A prospect list as a tile: name, additions this week, who made it and the last 14 days of entries. */
export function ProspectListTile({ row, to }: { row: ProspectListOverview; to: string }) {
  return (
    <LinkTile
      to={to}
      title={row.name}
      titleTooltip={row.name}
      muted={row.archived}
      adornment={
        <>
          <AgentActivity runs={row.active_agent_runs} />
          {row.added_7d > 0 && <DeltaChip value={row.added_7d} label="/wk" />}
        </>
      }
      meta={
        <>
          {row.created_by_user_id && (
            <UserAvatar
              displayName={row.created_by_display_name}
              email={row.created_by_email}
              pictureUrl={row.created_by_picture_url}
              size={20}
            />
          )}
          {row.daily_added_14d?.some((v) => v > 0) && <Sparkline values={row.daily_added_14d} />}
          <Typography variant="caption" sx={{ color: "text.secondary", ml: "auto" }}>
            {formatNumber(row.prospect_count)} total
          </Typography>
        </>
      }
    />
  );
}
